import {
  CommonMessages,
  EntityNotFound,
  NewPageWrapper,
  ServerError,
  useTitle,
} from '../../common';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Flexbox, Typography } from '@hiiretail/synergy-ui';
import { useIntl } from 'react-intl';
import { useUserSearchForm } from './hooks/useUserSearchForm';
import {
  DataRow,
  IdleContainer,
  LastRow,
  LeftColumn,
  NoSearchText,
  RightColumn,
  SearchButton,
  SearchWrapper,
  StyledTextarea,
  SvgContainer,
} from './UserSearch.styles';
import { UserDto, UserType } from '../../api-client';
import { Spinner } from '../../common/components/Spinner/Spinner';
import { Card } from '../../common/components/Card/Card';
import { CreateUserModal } from './ui';
import { useCreateUserModal } from './hooks/useCreateUserModal';
import { useHistory, useLocation } from 'react-router-dom';
import { Status } from '../../common/components/Status/Status';
import { getSearchValue } from './utils/utils';

export const UserSearch: React.FC = () => {
  useTitle('Find User');
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const [user, setUser] = useState<UserDto | null>(null);
  const {
    formState,
    errors,
    onChange,
    onSubmit,
    isSearching,
    onSearchUser,
    onSearchUserById,
    onSearchUserByExternalId,
  } = useUserSearchForm({
    setUser,
  });

  const { isCreateUserModalOpen, closeCreateUserDialog, createUser, createError, isCreating } =
    useCreateUserModal(isSearching);

  const displayMoreInformation = () => history.push(`users/${user?.id}`);

  const userSearchParam = useMemo(() => {
    const name = globalThis.decodeURI(getSearchValue(location.search, 'name') ?? '');
    const email = globalThis.decodeURI(getSearchValue(location.search, 'email') ?? '');
    const externalId = getSearchValue(location.search, 'externalId');
    const internalId = getSearchValue(location.search, 'id');
    const userProfileId = getSearchValue(location.search, 'userProfileId');

    return {
      name,
      email,
      externalId,
      internalId,
      userProfileId,
    };
  }, [location.search]);

  const isUserSearchParamPresent = useMemo(
    () => Object.values(userSearchParam).some((query) => !!query),
    [userSearchParam],
  );
  const searchUsersByParams = useCallback(() => {
    const { name, email, externalId, internalId, userProfileId } = userSearchParam;

    if (email || name) {
      return onSearchUser({ name: name ? name : undefined, email: email ? email : undefined });
    }
    if (userProfileId) {
      return onSearchUser({ userProfileId: userProfileId });
    }
    if (externalId) {
      return onSearchUserByExternalId(externalId);
    }
    if (internalId) {
      return onSearchUserById(internalId);
    }
  }, [onSearchUser, onSearchUserByExternalId, onSearchUserById, userSearchParam]);

  useEffect(() => {
    searchUsersByParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NewPageWrapper>
      {isCreateUserModalOpen && (
        <CreateUserModal
          onClose={closeCreateUserDialog}
          isOpen={isCreateUserModalOpen}
          isCreating={isCreating}
          error={createError}
          createUser={createUser}
        />
      )}
      <SearchWrapper container item flex="1 1 auto" justifyContent="space-between">
        <Card
          title="Find user"
          styles={{
            flexBasis: '35%',
          }}
        >
          <form>
            <StyledTextarea
              name="name"
              placeholder="e.g. Ola Nordmann"
              label={intl.formatMessage(CommonMessages.name)}
              type="text"
              autoComplete="name"
              value={userSearchParam.name ? userSearchParam.name : formState.name}
              onChange={onChange('name')}
              onFocus={userSearchParam.name ? onChange('name') : undefined}
              autoFocus={!!userSearchParam.name}
              disabled={!!formState.externalId || !!formState.id || !!formState.userProfileId}
              fullWidth
            />
            <StyledTextarea
              id="email"
              name="email"
              placeholder="e.g. ola.nordmann@extendaretail.com"
              label={intl.formatMessage(CommonMessages.emailAddress)}
              type="text"
              autoComplete="email"
              value={userSearchParam.email ? userSearchParam.email : formState.email}
              onChange={onChange('email')}
              onFocus={userSearchParam.email ? onChange('email') : undefined}
              autoFocus={!!userSearchParam.email}
              error={errors.email as string}
              disabled={!!formState.externalId || !!formState.id || !!formState.userProfileId}
              fullWidth
            />
            <StyledTextarea
              name="externalId"
              placeholder="e.g. 2894hj6yt789um1c4b22dcl990"
              label={intl.formatMessage(CommonMessages.externalId)}
              type="text"
              value={userSearchParam.externalId ?? formState.externalId}
              onChange={onChange('externalId')}
              onFocus={userSearchParam.externalId ? onChange('externalId') : undefined}
              autoFocus={!!userSearchParam.externalId}
              disabled={
                !!formState.name || !!formState.email || !!formState.id || !!formState.userProfileId
              }
              fullWidth
            />
            <StyledTextarea
              name="id"
              placeholder="e.g. CkCsMko13xMaDa2Ml0qWwe9201Ssd21"
              label={intl.formatMessage(CommonMessages.id)}
              type="text"
              value={userSearchParam.internalId ?? formState.id}
              onChange={onChange('id')}
              onFocus={userSearchParam.internalId ? onChange('id') : undefined}
              autoFocus={!!userSearchParam.internalId}
              disabled={
                !!formState.name ||
                !!formState.email ||
                !!formState.externalId ||
                !!formState.userProfileId
              }
              fullWidth
              last
            />
            <StyledTextarea
              name="userProfileId"
              placeholder="e.g. 0b983bd8awasd123fc0b4259970909b9c8f812379281778123fceedbf9d8c47cf256"
              label={intl.formatMessage(CommonMessages.userProfileId)}
              type="text"
              value={userSearchParam.userProfileId ?? formState.userProfileId}
              onChange={onChange('userProfileId')}
              onFocus={userSearchParam.userProfileId ? onChange('userProfileId') : undefined}
              autoFocus={!!userSearchParam.userProfileId}
              disabled={
                !!formState.name || !!formState.email || !!formState.externalId || !!formState.id
              }
              fullWidth
              last
            />
            <Flexbox container item justifyContent="flex-end" alignItems="center">
              <SearchButton
                type="submit"
                onClick={(e) => {
                  e.preventDefault();
                  onSubmit();
                }}
                disabled={
                  isUserSearchParamPresent && Object.values(formState).length
                    ? !isUserSearchParamPresent
                    : !!errors.email || isSearching || !Object.values(formState).length
                }
              >
                Search
              </SearchButton>
            </Flexbox>
          </form>
        </Card>
        <Card styles={{ flexBasis: '60%' }}>
          {
            //  Search results
            user && !isSearching && (
              <>
                <Typography variant="header2">Search results</Typography>
                <Flexbox container mt={2} direction="column">
                  <DataRow container item alignItems="center">
                    <LeftColumn>Name</LeftColumn>
                    <RightColumn variant="body2">{user.name}</RightColumn>
                  </DataRow>
                  <DataRow container item alignItems="center">
                    <LeftColumn>Email</LeftColumn>
                    <RightColumn variant="body2">{user.email}</RightColumn>
                  </DataRow>
                  <DataRow container item alignItems="center">
                    <LeftColumn>Type</LeftColumn>
                    <RightColumn variant="body2">{user.type}</RightColumn>
                  </DataRow>
                  <DataRow container item alignItems="center">
                    <LeftColumn>Status</LeftColumn>
                    <RightColumn variant="body2">
                      <Status status={user.status} variant="body2" />
                    </RightColumn>
                  </DataRow>
                  <DataRow container item alignItems="center">
                    <LeftColumn>ID</LeftColumn>
                    <RightColumn variant="body2">{user.id}</RightColumn>
                  </DataRow>
                  <DataRow container item alignItems="center">
                    <LeftColumn>User Profile ID</LeftColumn>
                    <RightColumn variant="body2">{user.userProfileId}</RightColumn>
                  </DataRow>
                  {user.type === UserType.Standalone && (
                    <DataRow container item alignItems="center">
                      <LeftColumn>Operator ID</LeftColumn>
                      <RightColumn variant="body2">
                        {user.operatorId || 'No operator id'}
                      </RightColumn>
                    </DataRow>
                  )}
                  <DataRow container item alignItems="center">
                    <LeftColumn>External ID</LeftColumn>
                    <RightColumn variant="body2">{user.externalId || 'No external id'}</RightColumn>
                  </DataRow>
                  <LastRow item>
                    <Button onClick={displayMoreInformation}>More information</Button>
                  </LastRow>
                </Flexbox>
              </>
            )
          }
          {
            //  Idle screen
            !user && !errors.fetchError && !isSearching && (
              <IdleContainer
                container
                item
                flex="1 1 auto"
                alignItems="center"
                justifyContent="center"
                direction="column"
              >
                <SvgContainer
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M26.7979 0.931369C27.8734 0.811477 28.9659 0.75 30.0718 0.75C31.1777 0.75 32.2701 0.811477 33.3457 0.931369L35.5818 1.18063L35.0833 5.65293L32.8471 5.40367C31.9368 5.30219 31.0108 5.25 30.0718 5.25C29.1327 5.25 28.2068 5.30219 27.2964 5.40367L25.0603 5.65293L24.5617 1.18063L26.7979 0.931369ZM46.5522 5.72742L48.3104 7.13139C50.0209 8.4972 51.5746 10.0509 52.9404 11.7613L54.3444 13.5196L50.8279 16.3275L49.4239 14.5693C48.2672 13.1206 46.9511 11.8046 45.5025 10.6479L43.7443 9.24389L46.5522 5.72742ZM16.3993 9.24389L14.6411 10.6479C13.1924 11.8046 11.8764 13.1206 10.7196 14.5693L9.31567 16.3275L5.7992 13.5196L7.20316 11.7613C8.56898 10.0509 10.1227 8.4972 11.8331 7.13139L13.5914 5.72742L16.3993 9.24389ZM5.72471 24.9885L5.47545 27.2247C5.37397 28.135 5.32178 29.061 5.32178 30C5.32178 30.939 5.37397 31.865 5.47545 32.7753L5.72471 35.0115L1.25241 35.51L1.00315 33.2739C0.883255 32.1983 0.821777 31.1059 0.821777 30C0.821777 28.8941 0.883255 27.8017 1.00315 26.7261L1.25241 24.49L5.72471 24.9885ZM58.8911 24.49L59.1404 26.7261C59.2603 27.8017 59.3218 28.8941 59.3218 30C59.3218 31.1059 59.2603 32.1983 59.1404 33.2739L58.8911 35.51L54.4188 35.0115L54.6681 32.7753C54.7696 31.865 54.8218 30.939 54.8218 30C54.8218 29.061 54.7696 28.135 54.6681 27.2247L54.4188 24.9885L58.8911 24.49ZM9.31567 43.6725L10.7196 45.4307C11.8764 46.8794 13.1924 48.1954 14.6411 49.3521L16.3993 50.7561L13.5914 54.2726L11.8331 52.8686C10.1227 51.5028 8.56898 49.9491 7.20317 48.2387L5.7992 46.4804L9.31567 43.6725ZM54.3444 46.4804L52.9404 48.2387C51.5746 49.9491 50.0209 51.5028 48.3104 52.8686L46.5522 54.2726L43.7443 50.7561L45.5025 49.3521C46.9511 48.1954 48.2672 46.8794 49.4239 45.4307L50.8279 43.6725L54.3444 46.4804ZM25.0603 54.3471L27.2964 54.5963C28.2068 54.6978 29.1327 54.75 30.0718 54.75C31.0108 54.75 31.9368 54.6978 32.8471 54.5963L35.0833 54.3471L35.5818 58.8194L33.3457 59.0686C32.2701 59.1885 31.1777 59.25 30.0718 59.25C28.9659 59.25 27.8734 59.1885 26.7979 59.0686L24.5617 58.8194L25.0603 54.3471Z"
                    fill="#9E9E9E"
                  />
                </SvgContainer>
                <NoSearchText>No search performed yet</NoSearchText>
              </IdleContainer>
            )
          }
          {
            //  Spinner
            isSearching && <Spinner dataTestId="spinner" />
          }
          {
            // @ts-ignore
            !isSearching && errors.fetchError?.response?.status !== 404 && (
              <ServerError error={errors.fetchError as string} />
            )
          }
          {
            // @ts-ignore
            !isSearching && errors.fetchError?.response?.status === 404 && (
              <EntityNotFound error={errors.fetchError as string} itemTitle="User" />
            )
          }
        </Card>
      </SearchWrapper>
    </NewPageWrapper>
  );
};
